import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { messages } from './messages';
import { useZoneComparisonMetrics } from './use-zone-comparison-metrics';
import style from './zone-comparison-metrics-table.module.less';

const headers = [
  { message: messages.metricColumnHeader },
  { message: messages.yourWarehouseColumnHeader },
  { message: messages.selectedWarehousesColumnHeader },
];

const rows = [
  {
    key: 'fulfillmentTime',
    labelMessage: messages.transitTimeMetric,
    format: (intl, value) => intl.formatMessage(messages.daysLabel, { days: value.toFixed(1) }),
  },
  {
    key: 'distance',
    labelMessage: messages.shipDistanceMetric,
    format: (intl, value) => intl.formatMessage(messages.milesLabel, { miles: Math.round(value) }),
  },
  {
    key: 'zone',
    labelMessage: messages.shippingZoneMetric,
    format: (intl, value) => value.toFixed(2),
  },
];

/**
 * @param {object} props
 * @param {Map<string, number>} props.baselineStateZoneMap
 * @param {Map<string, number>} props.currentStateZoneMap
 * @returns {React.ReactElement} The metrics comparison table component
 */
export const ZoneComparisonMetricsTable = ({ baselineStateZoneMap, currentStateZoneMap }) => {
  const intl = useIntl();
  const metrics = useZoneComparisonMetrics(baselineStateZoneMap, currentStateZoneMap);

  return (
    <div className={style['comparison-table-container']}>
      <table className={style['comparison-table']}>
        <thead>
          <tr>
            {headers.map((header) => (
              <th key={header.message.id} className={style['comparison-table-header']}>
                <FormattedMessage {...header.message} />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const metric = metrics[row.key];

            const before = metric.before ? row.format(intl, metric.before) : '-';
            const after = metric.after ? row.format(intl, metric.after) : '-';
            const shouldShowImprovement = metric.improvement !== null && metric.after < metric.before;

            return (
              <tr key={row.key} className={style['comparison-table-body-row']}>
                <td className={style['comparison-table-body-cell']}>
                  <FormattedMessage {...row.labelMessage} />
                </td>
                <td className={style['comparison-table-body-cell']}>{before}</td>
                <td className={style['comparison-table-body-cell']}>
                  <span className={style['comparison-table-metric-cell']}>
                    <span>{after}</span>
                    {shouldShowImprovement ? (
                      <span className={style['metric-improvement-tag']}>{`-${metric.improvement.toFixed(0)}%`}</span>
                    ) : null}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
